import { useState, useMemo } from "react";
import clsx from "clsx";

// MUI
import { Dialog, DialogTitle, DialogActions, IconButton, Divider, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

// Components
import Button from "@/Layouts/components/Buttons/Button";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Interfaces
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Helpers
import { PositionDialogContent } from "./PositionDialogContent";
import { TabsSplitPayPeriod } from "./TabsSplitPayPeriod";
import { Nullable } from "@/Common/interfaces/types";
import { IIsConfirmDisabledBtn, IPositionsDialogProps } from "@/Washouts/types/PositionsDialog";
import { and } from "@/Common/helpers/sonar";

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

const getPositions = (payPeriod: Nullable<IPayPeriod>, disabledPositions: string[]) => {
	if (!payPeriod?.positionStatus?.length) {
		return [];
	}
	return payPeriod.positionStatus?.map((ppItem) => {
		return {
			name: ppItem.position,
			disabled: disabledPositions.includes(ppItem.position),
		};
	});
};
const isConfirmDisabledBtn = (args: IIsConfirmDisabledBtn) => {
	const { hasPartnerPeriod, selectedPositions, selectedPartnerPositions, isPendingMutation } = args;
	if (!hasPartnerPeriod) {
		return selectedPositions.length === 0 || isPendingMutation;
	}

	return (selectedPositions.length === 0 && selectedPartnerPositions.length === 0) || isPendingMutation;
};

export default function PositionsDialog(props: Readonly<IPositionsDialogProps>) {
	const {
		open,
		action,
		isPendingMutation = false,
		currentPayPeriod,
		partnerPayPeriod,
		layoutDialogProps = { title: "Approve Positions", btnConfirmText: "Approve", btnCancelText: "Cancel" },
		isUsingCallback = false,
		setOpenPositionsSelector,
	} = props;

	const { payPeriod, commissionCycleId, disabledPositions = [] } = currentPayPeriod ?? {};

	console.log({ currentPayPeriod, partnerPayPeriod });

	const {
		payPeriod: partnerPayPeriodValue,
		commissionCycleId: partnerCommissionCycleId,
		disabledPositions: partnerDisabledPositions = [],
	} = partnerPayPeriod ?? {};

	const hasPartnerPeriod = !!partnerPayPeriodValue;

	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
	const [selectedPartnerPositions, setSelectedPartnerPositions] = useState<string[]>([]);
	const [selectedTabs, setSelectedTabs] = useState<number>(0);

	const currentPayPeriodPosition = useMemo(() => getPositions(payPeriod, disabledPositions), [payPeriod, disabledPositions]);
	const partnerPayPeriodPosition = useMemo(
		() => getPositions(partnerPayPeriodValue, partnerDisabledPositions),
		[partnerPayPeriodValue, partnerDisabledPositions]
	);

	const enabledPositions = useMemo(() => currentPayPeriodPosition.filter((p) => !p.disabled), [currentPayPeriodPosition]);
	const partnerEnabledPositions = useMemo(() => partnerPayPeriodPosition.filter((p) => !p.disabled), [partnerPayPeriodPosition]);

	const checkedAll = useMemo(
		() => enabledPositions.length > 0 && selectedPositions.length === enabledPositions.length,
		[selectedPositions, enabledPositions]
	);

	const checkedAllPartner = useMemo(
		() => partnerEnabledPositions.length > 0 && selectedPartnerPositions.length === partnerEnabledPositions.length,
		[selectedPartnerPositions, partnerEnabledPositions]
	);

	const handleCheckAll = (checked: boolean) => {
		if (hasPartnerPeriod && selectedTabs === 1) {
			if (checked) {
				setSelectedPartnerPositions(partnerEnabledPositions.map((p) => p.name));
			} else {
				setSelectedPartnerPositions([]);
			}
			return;
		}

		if (checked) {
			setSelectedPositions(enabledPositions.map((p) => p.name));
		} else {
			setSelectedPositions([]);
		}
	};

	const handleTogglePosition = (positionName: string) => {
		if (!positionName) {
			return;
		}

		setSelectedPositions((prev) => {
			if (prev.includes(positionName)) {
				return prev.filter((name) => name !== positionName);
			}
			return [...prev, positionName];
		});
	};

	const handleChangeParentPayPeriod = (partnerPositionName?: string) => {
		if (!partnerPositionName) {
			return;
		}

		setSelectedPartnerPositions((prev) => {
			if (prev.includes(partnerPositionName)) {
				return prev.filter((p) => p !== partnerPositionName);
			}
			return [...prev, partnerPositionName];
		});
	};

	const handleConfirm = () => {
		if (and(!selectedPositions?.length, !selectedPartnerPositions?.length)) {
			return;
		}

		if (isUsingCallback) {
			action({
				selectedPositionStatus: selectedPositions,
				selectedPartnerPositionStatus: selectedPartnerPositions,
			});
		} else {
			if (selectedPositions?.length) {
				action({ commissionCycleId: commissionCycleId || "", payPeriodId: payPeriod?._id ?? "", positions: selectedPositions });
			}

			if (and(hasPartnerPeriod, Boolean(selectedPartnerPositions?.length))) {
				action({
					commissionCycleId: partnerCommissionCycleId || "",
					payPeriodId: partnerPayPeriodValue?._id ?? "",
					positions: selectedPartnerPositions,
				});
			}
		}
		handleClose();
	};

	const handleClose = () => {
		setSelectedPositions([]);
		setSelectedPartnerPositions([]);
		setSelectedTabs(0);
		setOpenPositionsSelector(false);
	};

	const isConfirmDisabled = isConfirmDisabledBtn({
		hasPartnerPeriod,
		selectedPositions,
		selectedPartnerPositions,
		isPendingMutation,
	});

	const tabs = [
		{ title: payPeriod?.title, disabled: currentPayPeriodPosition?.length === 0 || false, value: "0" },
		{ title: partnerPayPeriodValue?.title, disabled: partnerPayPeriodPosition?.length === 0 || false, value: "1" },
	];

	const badgeValues: Record<number, number> = {
		0: selectedPositions.length,
		1: selectedPartnerPositions.length,
	};

	const memoizedContentMap = useMemo(
		() => [
			<PositionDialogContent
				key={`${commissionCycleId}_${payPeriod?._id}`}
				sx={{ marginTop: "24px", marginLeft: "-25px" }}
				payPeriodPosition={currentPayPeriodPosition}
				isCheckedAll={checkedAll}
				handleCheckedAll={handleCheckAll}
				selectedPositions={selectedPositions}
				handleTogglePosition={handleTogglePosition}
			/>,
			<PositionDialogContent
				sx={{ marginTop: "24px", marginLeft: "-25px" }}
				key={`${partnerCommissionCycleId}_${partnerPayPeriodValue?._id}`}
				payPeriodPosition={partnerPayPeriodPosition}
				isCheckedAll={checkedAllPartner}
				handleCheckedAll={handleCheckAll}
				selectedPositions={selectedPartnerPositions}
				handleTogglePosition={handleChangeParentPayPeriod}
			/>,
		],
		[tabs, selectedPositions, selectedPartnerPositions]
	);

	return (
		<BootstrapDialog open={open} onClose={handleClose} disableEscapeKeyDown>
			<DialogTitle marginTop={1} marginLeft={1} marginBottom={hasPartnerPeriod ? 2 : 0}>
				{layoutDialogProps.title}
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: "absolute",
						right: 5,
						top: 5,
						color: (theme) => theme.palette.text.secondary,
					}}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			{hasPartnerPeriod && (
				<>
					<TabsSplitPayPeriod
						badgeValues={badgeValues}
						tabs={tabs}
						tabSelected={selectedTabs}
						setTabSelected={setSelectedTabs}
						memoizedContentMap={memoizedContentMap}
					/>
					<Divider
						sx={{
							minWidth: 456,
							maxHeight: 0,
							marginTop: 4,
							marginLeft: 4,
							marginRight: 4,
							border: "1px solid var(--General-General-Stroke, #CCCCCC)",
						}}
					/>
				</>
			)}
			{!hasPartnerPeriod && (
				<PositionDialogContent
					payPeriodPosition={currentPayPeriodPosition}
					isCheckedAll={checkedAll}
					handleCheckedAll={handleCheckAll}
					selectedPositions={selectedPositions}
					handleTogglePosition={handleTogglePosition}
				/>
			)}

			<DialogActions sx={{ marginTop: 3, marginBottom: 4, px: "32px", justifyContent: "flex-end" }}>
				<Stack direction="row" spacing={3}>
					<Button
						variant="contained"
						classes="!bg-gray-blue h-[30px] rounded-[6px] !shadow-none hover:!shadow-none !font-sans !capitalize !text-white !text-[14px]"
						text={layoutDialogProps.btnCancelText ?? "Cancel"}
						sx={{ minWidth: "82px", width: "100px", minHeight: "30px", height: "30px" }}
						onClick={handleClose}
					/>
					<Button
						variant="contained"
						classes={clsx({
							"h-[30px] rounded-[6px] !shadow-none !font-sans !capitalize !text-white !text-[14px]": true,
							"!bg-primary hover:!shadow-primary-shadow-50": !isConfirmDisabled,
							"!bg-disabled !cursor-default !shadow-none hover:!shadow-none": isConfirmDisabled,
						})}
						sx={{ minWidth: "82px", width: "100px", height: "30px", minHeight: "30px" }}
						text={layoutDialogProps.btnConfirmText}
						disabled={isConfirmDisabled}
						onClick={handleConfirm}
					/>
				</Stack>
			</DialogActions>
		</BootstrapDialog>
	);
}
